import classnames from 'clsx';
import { uniqueId } from 'lodash';
import type { FC, HTMLAttributes, ReactNode } from 'react';
import React, { useCallback, useContext } from 'react';

import OptionContext from './OptionContext';
import styles from './index.scss';

interface ToggleOptionProps extends Omit<HTMLAttributes<HTMLInputElement>, 'onClick'> {
  value: string;
  disabled?: boolean;
  name?: string;
  onClick?: (value: string) => void;
  icon?: ReactNode;
}

const Option: FC<ToggleOptionProps> = ({
  id: idProp,
  value,
  children,
  disabled: disabledProp,
  className,
  onChange: onChangeProp,
  onClick: onClickProp,
  name: nameProp,
  icon,
  ...other
}) => {
  const {
    selectedValue,
    disabled: disabledContext,
    name: nameContext,
    onChange: onChangeContext,
    onClick: onClickContext,
    selectedRef
  } = useContext(OptionContext);

  const checked = value === selectedValue;
  const disabled = disabledProp || disabledContext;
  const name = nameProp || nameContext;
  const id = idProp || uniqueId('widget-toggle-option');

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onChangeProp) {
        onChangeProp(e);
      }
      if (onChangeContext) {
        onChangeContext(e);
      }
    },
    [onChangeProp, onChangeContext]
  );

  const handleClick = useCallback(() => {
    if (onClickProp) {
      onClickProp(value);
    }
    if (onClickContext) {
      onClickContext(value);
    }
  }, [onClickContext, onClickProp, value]);

  return (
    <>
      <input
        id={id}
        className={classnames(styles.toggleOptionInput, className)}
        type="radio"
        name={name}
        checked={checked}
        value={value}
        disabled={disabled}
        onChange={handleChange}
        data-test={checked ? 'toggle:selected' : null}
        ref={checked ? selectedRef : null}
        {...other}
      />
      <label
        htmlFor={id}
        onClick={handleClick}
        data-test={checked ? 'toggle:selected:label' : null}
        className={classnames({
          [styles.toggleOptionLabel]: true,
          [styles.toggleOptionLabelSelected]: checked
        })}
      >
        {icon}
        <span>{children}</span>
      </label>
    </>
  );
};

Option.displayName = 'WidgetToggleOption';
export default Option;
